/**
 * Press-down prefetch for in-app navigation. The bottom-nav tabs already
 * idle-prefetch in ConnectPortalLayout, but tapping a deeply nested link
 * (e.g. Top-up from Wallet, Notifications from Header) would still pay
 * the chunk-download + parse cost on tap. Calling `prefetchRoute(path)`
 * on `onPointerDown` warms the chunk during the ~80ms finger-down → tap
 * gap, so by the time React Router commits the new route, it's already
 * in memory.
 *
 * Map keys are pathname prefixes; longest match wins. Add entries here
 * as new heavy pages are added. Missing entries are silently no-ops
 * (regular React Router lazy load).
 */
const ROUTE_LOADERS: Array<[string, () => Promise<unknown>]> = [
  ["/connect-portal/dashboard", () => import("@/pages/connect-portal/ConnectDashboard")],
  ["/connect-portal/contacts", () => import("@/pages/connect-portal/ConnectContactsPage")],
  ["/connect-portal/history", () => import("@/pages/connect-portal/ConnectHistoryPage")],
  ["/connect-portal/topup", () => import("@/pages/connect-portal/ConnectTopUpPage")],
  ["/connect-portal/rates", () => import("@/pages/connect-portal/ConnectRatesPage")],
  ["/connect-portal/notifications", () => import("@/pages/connect-portal/ConnectNotificationsPage")],
  ["/connect-portal/me", () => import("@/pages/connect-portal/ConnectMePage")],
  ["/connect-portal/messages", () => import("@/pages/connect-portal/ConnectMessagesPage")],
  ["/connect-portal/referrals", () => import("@/pages/connect-portal/ConnectReferralsPage")],
  ["/connect-portal/help", () => import("@/pages/connect-portal/ConnectHelpPage")],
];

const warmed = new Set<string>();

export function prefetchRoute(pathname: string): void {
  if (!pathname) return;
  // Strip query/hash; match on pathname only.
  const clean = pathname.split("?")[0].split("#")[0];
  // Find longest prefix match.
  let best: [string, () => Promise<unknown>] | null = null;
  for (const entry of ROUTE_LOADERS) {
    if (clean.startsWith(entry[0]) && (!best || entry[0].length > best[0].length)) {
      best = entry;
    }
  }
  if (!best) return;
  if (warmed.has(best[0])) return;
  warmed.add(best[0]);
  best[1]().catch(() => warmed.delete(best![0]));
}

